import { useEffect, useMemo } from "react";
import { Star, RefreshCw } from "lucide-react";
import { Stars } from "@/components/site/Stars";
import { useGoogleReviews } from "@/context/GoogleReviewsContext";

const GOOGLE_REVIEW_URL = "https://g.page/r/CdeH-owIuo6vEAI/review";

function GoogleReviewCard({ r }) {
  return (
    <div className="border border-line p-6 bg-ivory flex flex-col gap-3" data-testid="google-review-card">
      <div className="flex items-center gap-3">
        {r.profile_photo_url ? (
          <img src={r.profile_photo_url} alt={r.author_name} className="w-10 h-10 rounded-full object-cover" />
        ) : (
          <div className="w-10 h-10 rounded-full bg-ivory-100 flex items-center justify-center text-espresso font-serif">
            {(r.author_name || "?").slice(0, 1)}
          </div>
        )}
        <div className="min-w-0">
          <div className="font-serif text-espresso truncate">{r.author_name || "Google reviewer"}</div>
          <div className="label-xs text-espresso-light">{r.relative_time_description}</div>
        </div>
      </div>
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <Star
            key={n}
            size={16}
            className={n <= (r.rating || 0) ? "fill-gold text-gold" : "text-line"}
            strokeWidth={1.5}
          />
        ))}
      </div>
      <p className="text-espresso text-sm leading-relaxed whitespace-pre-line">{r.text}</p>
    </div>
  );
}

export default function Reviews() {
  const { data, loading, refreshing, refresh } = useGoogleReviews();

  // If the visitor lands directly on /reviews and the snapshot is stale,
  // the context already schedules a Places JS fetch on mount. This effect
  // just makes sure we retry once more after a hydration if the initial
  // pass had no key handy.
  useEffect(() => {
    if (!loading && data && data.stale) refresh({ force: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const rating = data?.rating;
  const total = data?.total || 0;
  const reviews = useMemo(() => data?.reviews || [], [data]);

  return (
    <main data-testid="reviews-page" className="max-w-[1400px] mx-auto px-6 md:px-10 py-12 md:py-20">
      <div className="border-b border-line pb-10">
        <div className="label-xs text-espresso-light">Real people. Real tiramisù.</div>
        <h1 className="mt-4 font-serif text-5xl md:text-7xl leading-[0.95] text-espresso">
          What people<br/>
          <span className="italic text-espresso-light">actually think.</span>
        </h1>
        {rating != null && total > 0 && (
          <div className="mt-8 flex items-baseline gap-6 flex-wrap" data-testid="google-rating-summary">
            <Stars value={rating} size={22} showValue />
            <span className="text-espresso-light">
              Based on {total} Google review{total === 1 ? "" : "s"}
            </span>
            {refreshing && (
              <span className="label-xs text-espresso-light inline-flex items-center gap-1.5">
                <RefreshCw size={12} strokeWidth={1.5} className="animate-spin" />
                Refreshing…
              </span>
            )}
          </div>
        )}
      </div>

      <div className="mt-12">
        {loading ? (
          <p className="text-espresso-light label-xs">Loading Google reviews…</p>
        ) : reviews.length === 0 ? (
          <div className="py-20 text-center">
            <p className="font-serif italic text-2xl text-espresso-light">
              {data?.configured === false
                ? "Reviews will appear here once Google Places is configured."
                : "No Google reviews yet."}
            </p>
            <a
              href={GOOGLE_REVIEW_URL}
              target="_blank"
              rel="noreferrer"
              className="btn-espresso mt-8 inline-block"
              data-testid="write-review-empty-cta"
            >
              Be the first to review us on Google
            </a>
          </div>
        ) : (
          <>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="reviews-grid">
              {reviews.map((r, i) => <GoogleReviewCard key={r.time || i} r={r} />)}
            </div>
            <div className="mt-14 border-t border-line pt-10 text-center">
              <p className="font-serif italic text-espresso-light text-lg">Loved your order?</p>
              <a
                href={GOOGLE_REVIEW_URL}
                target="_blank"
                rel="noreferrer"
                className="btn-espresso mt-6 inline-block"
                data-testid="write-review-cta"
              >
                Write a Google review
              </a>
            </div>
          </>
        )}
      </div>
    </main>
  );
}
